/**
 * RFC 4180 CSV cell escaping and safety guards.
 *
 * Kept deliberately tiny and side-effect-free so the core is easy to audit and unit-test.
 */

/** Excel / Google Sheets / LibreOffice all honour a 32,767-character hard cap per cell. */
export const EXCEL_CELL_CHAR_LIMIT = 32_767;

/**
 * Cached regexes keyed by delimiter. A CSV cell needs quote-wrapping when it contains `"`, a line
 * break, or the active field delimiter — so the regex depends on what we picked for this file.
 */
const escapeRegexCache = new Map<string, RegExp>();

function getEscapeRegex(delimiter: string): RegExp {
  let r = escapeRegexCache.get(delimiter);
  if (!r) {
    const safe = delimiter.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    r = new RegExp(`["${safe}\\r\\n]`);
    escapeRegexCache.set(delimiter, r);
  }
  return r;
}

/**
 * Chars that trigger formula evaluation when the cell is opened in Excel. Tab and CR are included
 * because Excel also treats leading whitespace-then-formula as a formula.
 *
 * https://owasp.org/www-community/attacks/CSV_Injection
 */
const FORMULA_TRIGGER_CHARS = /^[=+\-@\t\r]/;

/**
 * Escape a single cell value per RFC 4180.
 *
 * - If the value contains `"`, the active delimiter (default `,`), `\r`, or `\n`, the result is
 *   wrapped in `"` and every internal `"` is doubled.
 * - Otherwise the value is returned unchanged — unescaped cells keep the file small and diff-friendly.
 */
export function csvEscape(value: string, delimiter: string = ','): string {
  if (value === '') return '';
  if (getEscapeRegex(delimiter).test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Prefix a value with a single quote when it begins with `=`, `+`, `-`, `@`, `\t`, or `\r`. Excel
 * interprets such cells as formulas and executes them — a well-documented CSV injection vector.
 *
 * Prefixing with `'` tells Excel to render the cell as literal text; the quote itself is hidden in
 * the UI but visible in the raw cell — an acceptable trade-off for safety.
 */
export function guardFormulaInjection(value: string): string {
  if (!value) return value;
  if (FORMULA_TRIGGER_CHARS.test(value)) {
    return `'${value}`;
  }
  return value;
}

/**
 * Clamp oversized cells to Excel's 32,767-char limit. Excel silently truncates longer cells on
 * open, which can corrupt multi-line content. We truncate explicitly and append an ellipsis so
 * the user can see the cell was shortened.
 */
export function truncateForExcel(
  value: string,
  ellipsis: string = '…'
): string {
  if (value.length <= EXCEL_CELL_CHAR_LIMIT) return value;
  const keep = Math.max(0, EXCEL_CELL_CHAR_LIMIT - ellipsis.length);
  return value.slice(0, keep) + ellipsis;
}
